// src/components/inputs/SelectAutoComplacent.jsx
import { Autocomplete, CircularProgress, TextField, useTheme } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AddIcon from '@mui/icons-material/Add';
import React from 'react';
// import { makePreview } from '../../utils/helpers'; // ❌ لم نعد بحاجة لهذا هنا
import { useTranslation } from 'react-i18next';

const SelectAutoComplacent = ({
  name,
  value,
  options,
  onChange,
  disabled,
  sx,
  error,
  helperText,
  creatable = false,
  onCreate,
  loading = false,
}) => {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const lng = i18n.language || 'en';
  const isArabic = lng === 'ar';

  const getLabel = React.useCallback((opt) => {
    if (!opt) return '';
    if (opt.__isCreate__) {
      return `${isArabic ? '+ إنشاء' : '+ Create'} "${opt.label}"`;
    }
    if (typeof opt.label === 'string') {
      return opt.label;
    }
    if (typeof opt.name === 'string') {
      return opt.name;
    }
    if (typeof opt.name === 'object' && opt.name) {
      return isArabic
        ? (opt.name.ar || opt.name.en || '')
        : (opt.name.en || opt.name.ar || '');
    }
    return '';
  }, [isArabic]);
  // ✅ إضافة حالة للتحكم في النص المدخل يدوياً لمنع الـ refilling المزعج
  const [localInputValue, setLocalInputValue] = React.useState('');

  // مزامنة النص عند تغيير القيمة المختارة من الخارج
  React.useEffect(() => {
    // const selectedOption = options?.find((o) => String(o.id) === String(value ?? ''));
    const selectedOption = options?.find(
      (o) =>
        String(o.id ?? o.value) ===
        String(value ?? '')
    );
    if (selectedOption) {
      setLocalInputValue(getLabel(selectedOption));
    } else if (!value) {
      setLocalInputValue('');
    }
  }, [value, options, getLabel]);
  const getOptionId = (option) =>
    option?.id ?? option?.value;
  const filterOptions = React.useCallback((opts, { inputValue }) => {
    const filtered = opts.filter(opt =>
      getLabel(opt).toLowerCase().includes(inputValue.toLowerCase())
    );
    if (creatable && inputValue && filtered.length === 0 && onCreate) {
      filtered.push({
        id: '__create__',
        label: inputValue,
        __isCreate__: true,
      });
    }
    return filtered;
  }, [creatable, onCreate, getLabel]);

  return (
    <Autocomplete
      id={`${name}-autocomplete`}
      options={options ?? []}
      getOptionLabel={getLabel}
      filterOptions={filterOptions}
      loading={loading}
      loadingText={isArabic ? 'جاري التحميل...' : 'Loading...'}
      isOptionEqualToValue={(option, val) => {
        if (option?.__isCreate__) return false;
        return String(option?.id ?? option?.value) ===
          String(val?.id ?? val?.value ?? val ?? '');
      }}
      value={
        options?.find(
          (o) => String(getOptionId(o)) === String(value ?? '')
        ) ?? null
      }
      inputValue={localInputValue}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === 'input' || reason === 'clear') {
          setLocalInputValue(newInputValue);
        }
      }}
      onChange={(_, newVal) => {
        if (newVal?.__isCreate__) {
          if (onCreate) onCreate(localInputValue);
          return;
        }
        if (!onChange) return;
        const syntheticEvent = {
          target: {
            name,
            value: newVal
              ? (newVal.id ?? newVal.value)
              : '',
          },
        };
        onChange(syntheticEvent);
        if (newVal) {
          setLocalInputValue(getLabel(newVal));
        } else {
          setLocalInputValue('');
        }
      }}
      disabled={disabled}
      clearOnEscape
      disableClearable={false}
      popupIcon={<ArrowDropDownIcon />}
      sx={{
        width: '100%',
        mt: 1,
        minWidth: '80px',
        '& .MuiAutocomplete-endAdornment': {
          right: isArabic ? 'auto !important' : '9px !important',
          left: isArabic ? '9px !important' : 'auto !important',
        },
        '& .MuiAutocomplete-clearIndicator': {
          marginRight: isArabic ? 'auto' : undefined,
          marginLeft: isArabic ? 0 : 'auto',
        },
        '& .MuiAutocomplete-popupIndicator': {
          marginRight: isArabic ? 'auto' : undefined,
          marginLeft: isArabic ? 0 : 'auto',
        },
        ...sx,
      }}

      renderOption={(props, option) => {
        if (option.__isCreate__) {
          return (
            <li {...props} style={{ whiteSpace: 'normal', wordBreak: 'break-word', display: 'flex', alignItems: 'center', gap: 4 }}>
              <AddIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              {getLabel(option)}
            </li>
          );
        }
        return (
          <li {...props} style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {getLabel(option)}
          </li>
        );
      }}

      ListboxProps={{
        sx: {
          maxHeight: 320,
          direction: isArabic ? 'rtl' : 'ltr',
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          size="small"
          error={!!error}
          helperText={error || helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
            sx: {
              height: 40,
              backgroundColor: theme.palette.background.input,
              borderRadius: 1,
              direction: isArabic ? 'rtl' : 'ltr',
              paddingRight: isArabic ? '9px !important' : '39px !important',
              paddingLeft: isArabic ? '39px !important' : '9px !important',
              '& input': {
                padding: '0 !important',
                textAlign: isArabic ? 'right' : 'left',
                '&::placeholder': {
                  textAlign: isArabic ? 'right' : 'left',
                },
              },
            },
          }}
        />
      )}
    />
  );
};

export default SelectAutoComplacent;